'use client';

import {useState} from 'react';
import {FileText} from 'lucide-react';
import {FlowStepHeading, FlowTextarea, FlowValidationError} from './flow_form';
import {FlowTakehomeUpload} from './flow_takehome_upload';
import {FlowStructuredQuestions} from './flow_structured_questions';
import type {TakeHomeFile} from 'iq-interview_experiences/types';

const STEP_QUESTION =
  'This is the part that helps everyone who comes after you. What exactly did they ask you? The specific technical questions, the case prompts, the weird take-home instructions. Anything you can remember, even if it feels small. The details are what make this useful.';

type Props = {
  step: number;
  totalSteps: number;
  value: string;
  onChange: (v: string) => void;
  validationError: string | null;
  allowTakehome?: boolean;
  useStructured?: boolean;
  takeHomeFiles?: TakeHomeFile[];
  onAddTakehomeFiles?: (files: File[]) => void;
  onRemoveTakehomeFile?: (id: string) => void;
};

export function FlowStepQuestionsAndTakehome({
  step,
  totalSteps,
  value,
  onChange,
  validationError,
  allowTakehome = true,
  useStructured = false,
  takeHomeFiles,
  onAddTakehomeFiles,
  onRemoveTakehomeFile,
}: Props) {
  const [showUpload, setShowUpload] = useState(false);

  return (
    <div className='flex flex-col gap-4'>
      <FlowStepHeading step={step} totalSteps={totalSteps}>
        {STEP_QUESTION}
      </FlowStepHeading>
      {useStructured ? (
        <FlowStructuredQuestions onChange={onChange} />
      ) : (
        <div>
          <FlowTextarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder='List as many as you can remember...'
            rows={7}
          />
          <p className='text-xs mt-1.5 text-iq-grey-medium'>
            Optional — include as many or as few as you remember.
          </p>
        </div>
      )}
      {allowTakehome && takeHomeFiles && onAddTakehomeFiles && onRemoveTakehomeFile && (
        <div className='rounded-lg bg-orange-50 border border-orange-200'>
          <button
            type='button'
            onClick={() => setShowUpload((v) => !v)}
            className='w-full inline-flex items-center gap-2 px-3 py-2.5 text-base text-iq-grey-medium hover:text-iq-black transition-colors'>
            <FileText className='w-4 h-4 shrink-0' />
            {showUpload ? (
              <span className='underline'>Hide take-home upload</span>
            ) : (
              <>
                Had a take-home assignment? <span className='underline'>Upload files</span> →
              </>
            )}
          </button>
          {showUpload && (
            <div className='px-3 pb-3'>
              <FlowTakehomeUpload
                files={takeHomeFiles}
                onAddFiles={onAddTakehomeFiles}
                onRemoveFile={onRemoveTakehomeFile}
              />
            </div>
          )}
        </div>
      )}
      {validationError && <FlowValidationError message={validationError} />}
    </div>
  );
}
